import store from "../../redux-store";
import { fetchOthersOffline, fetchTableOffline, saveOthersOffline } from "../../helpers/OfflineHelper/shared";
import { getItemsOffline, getOptionsetOffline } from "../../helpers/OfflineHelper";
import { getItemsData } from "../../redux-store/slices/ItemsSlice";
import {
  discountsDataCallComplete,
  getDiscountsData,
  getDiscountsDataSuccess,
  getOptionSetsData,
  setCatagoryDataSuccess,
  setMenuDataSuccess,
} from "../../redux-store/slices/catalogueSlice";
import {
  allResourceGroupCallComplete,
  allResourcesCallComplete,
  allTablesCallComplete,
  fetchPrinter,
  fetchPrinterSuccess,
  getAllResourceGroup,
  getAllResourceGroupSuccess,
  getAllResources,
  getAllResourcesSuccess,
  getAllTables,
  getAllTablesSuccess,
  printersCallComplete,
} from "../../redux-store/slices/TableViewSlice";
import { isOffline } from "./shared";

export * from "./shared";

/**
 * WEB variant of `facade/ServiceFacade`. See `./shared.ts` for what this folder is, and for
 * `isOffline` / `isSyncStarted` / `othersDataTypes` / `getCatalogueDataFn`, which are shared.
 *
 * The nine fetchers below all follow the same two-branch shape: offline they read out of the
 * local store and dispatch a `*Success` action themselves; online they dispatch the fetch action
 * and let the saga do it. They are per-variant rather than shared for two reasons, both real:
 *
 * 1. **Signature.** Each takes a `hideLoader` flag and threads it into the fetch action, which
 *    this app's sagas forward as the request's loader-suppression flag. The other platform's
 *    copies take no argument and its sagas ignore the payload, so a shared body would either
 *    change one platform's action payloads or drop the flag on this one.
 * 2. **The completion actions.** Six of these dispatch a no-op `*CallComplete` action after the
 *    offline branch fills state — a mechanism this app has and the other does not. A `shared.ts`
 *    cannot conditionally dispatch an action only one platform defines (the same reason
 *    `getOptionsetOffline` and `getItemsOffline` are duplicated in `helpers/OfflineHelper`).
 *
 * Collapse these onto `shared.ts` the day the other platform grows the completion actions and a
 * loader flag — not before.
 *
 * ⚠️ **`isOffline` is IMPORTED as well as re-exported** (via `export * from "./shared"`): every
 * function below calls it in this file's own body, and a re-export creates no local binding
 * (ledger rule 5 / hazard 30).
 *
 * ⚠️ Deep-imports the `.desktop` slice variants on purpose (hazard 6): the four `*CallComplete`
 * actions and `getItemsData`'s loader payload exist only on this side, so a bare folder import
 * would not type — and on the other platform would bind a slice that has no such action.
 */

export function getItemsFn(hideLoader = true) {
  if (isOffline()) {
    getItemsOffline();
    return;
  }
  // ⚠️ Cast, not a reducer change: these are 0-param reducers, so RTK types their creator as
  // taking nothing — but the creator ALWAYS accepts an argument at runtime and this app's saga
  // reads it off `action.payload`. That is ledger hazard 14, and TableViewSlice/shared.ts
  // records the standing decision NOT to widen the reducer to silence it. The app's untyped
  // `.js` passed the same argument; this preserves it exactly.
  store.dispatch((getItemsData as any)(hideLoader));
}

export function getOptionSetsFn(hideLoader = true) {
  if (isOffline()) {
    getOptionsetOffline();
    return;
  }
  // ⚠️ Cast, not a reducer change: these are 0-param reducers, so RTK types their creator as
  // taking nothing — but the creator ALWAYS accepts an argument at runtime and this app's saga
  // reads it off `action.payload`. That is ledger hazard 14, and TableViewSlice/shared.ts
  // records the standing decision NOT to widen the reducer to silence it. The app's untyped
  // `.js` passed the same argument; this preserves it exactly.
  store.dispatch((getOptionSetsData as any)(hideLoader));
}

export function getDiscountsFn(hideLoader?: any) {
  function callOffline() {
    fetchOthersOffline({
      type: 'discount',
      callback: (discount: any) => {
        store.dispatch(getDiscountsDataSuccess({
          response: discount
        }))
        store.dispatch(discountsDataCallComplete());
      }
    });
  }
  if (isOffline()) {
    callOffline();
    return;
  }
  store.dispatch(getDiscountsData(hideLoader));
}

export function getResourceFn(ip?: any) {
  function callOffline() {
    fetchOthersOffline({
      type: 'resource',
      callback: (resource: any) => {
        store.dispatch(getAllResourcesSuccess(resource))
        store.dispatch(allResourcesCallComplete());
      }
    });
  }
  if (isOffline()) {
    callOffline();
    return;
  }
  store.dispatch(getAllResources(ip));
}

export function getResourceGroupsFn(hideLoader = true) {
  function callOffline() {
    fetchOthersOffline({
      type: 'resourcegrroup',
      callback: (resourceGroups: any) => {
        store.dispatch(getAllResourceGroupSuccess(resourceGroups))
        store.dispatch(allResourceGroupCallComplete());
      }
    });
  }
  if (isOffline()) {
    callOffline();
    return;
  }
  store.dispatch(getAllResourceGroup(hideLoader));
}

export function getCategoryDataFn(ip?: any) {
  function callOffline() {
    fetchOthersOffline({
      type: 'category',
      callback: (category: any) => {
        store.dispatch(setCatagoryDataSuccess({
          categories: category
        }))
      }
    });
  }
  if (isOffline()) {
    callOffline();
    return;
  }
  store.dispatch(setCatagoryDataSuccess(ip));
  saveOthersOffline({
    type: 'category',
    items: ip.categories
  });
}

export function getMenuDataFn(ip?: any) {
  function callOffline() {
    fetchOthersOffline({
      type: 'menu',
      callback: (menu: any) => {
        store.dispatch(setMenuDataSuccess({
          menus: menu
        }))
      }
    });
  }
  if (isOffline()) {
    callOffline();
    return;
  }
  store.dispatch(setMenuDataSuccess(ip));
  saveOthersOffline({
    type: 'menu',
    items: ip.menu
  });
}

export function getAllTablesFn(ip?: any) {
  function callOffline() {
    fetchTableOffline({
      cb: (res: any) => {
        store.dispatch(getAllTablesSuccess(res))
        store.dispatch(allTablesCallComplete());
      }
    });
  }
  if (isOffline()) {
    callOffline();
    return;
  }
  store.dispatch(getAllTables(ip));
}

export function getPrintersFn(ip?: any) {
  function callOffline() {
    fetchOthersOffline({
      type: 'printer',
      callback: (printers: any) => {
        store.dispatch(fetchPrinterSuccess(printers));
        store.dispatch(printersCallComplete());
      }
    });
  }
  if (isOffline()) {
    callOffline();
    return;
  }
  store.dispatch(fetchPrinter(ip));
}
